import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaHome, 
  FaSearch, 
  FaPlus, 
  FaUser, 
  FaBars,
  FaTimes,
  FaComments,
  FaBell,
  FaSignOutAlt,
  FaBook,
  FaCog,
  FaQuestionCircle,
  FaInfoCircle,
  FaPlay
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const NavigatorContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #000;
`;

const Sidebar = styled.div`
  width: 250px;
  background-color: #111;
  border-right: 1px solid #333;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  left: ${props => props.isOpen ? '0' : '-250px'};
  transition: left 0.3s ease;
  z-index: 1000;

  @media (max-width: 768px) {
    width: 100%;
    left: ${props => props.isOpen ? '0' : '-100%'};
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${props => props.sidebarOpen ? '250px' : '0'};
  transition: margin-left 0.3s ease;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.h1`
  color: #4CAF50;
  font-size: 24px;
  font-weight: bold;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const NavMenu = styled.nav`
  flex: 1;
  padding: 20px 0;
`;

const NavItem = styled.div`
  padding: 12px 20px;
  color: ${props => props.active ? '#4CAF50' : '#fff'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background-color 0.2s ease;
  border-left: 3px solid ${props => props.active ? '#4CAF50' : 'transparent'};

  &:hover {
    background-color: #222;
  }

  svg {
    font-size: 18px;
  }
`;

const UserSection = styled.div`
  padding: 20px;
  border-top: 1px solid #333;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  color: #fff;
  font-weight: 500;
  font-size: 14px;
`;

const UserEmail = styled.div`
  color: #ccc;
  font-size: 12px;
`;

const NavSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #333;
`;

const NavSectionTitle = styled.div`
  color: #666;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 20px 12px 20px;
  margin-bottom: 8px;
`;

const LogoutSection = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #333;
`;

const LogoutButton = styled.button`
  background-color: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  &:hover {
    background-color: #45a049;
  }
`;

const MobileHeader = styled.div`
  display: none;
  padding: 16px;
  background-color: #111;
  border-bottom: 1px solid #333;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};

  @media (min-width: 769px) {
    display: none;
  }
`;

const AppNavigator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const navItems = [
    { path: '/feed', label: 'Feed', icon: FaHome },
    { path: '/loops', label: 'Loops', icon: FaPlay },
    { path: '/search', label: 'Search', icon: FaSearch },
    { path: '/create', label: 'Create Post', icon: FaPlus },
    { path: '/chat', label: 'Chat', icon: FaComments },
    { path: '/notifications', label: 'Notifications', icon: FaBell },
    { path: '/profile', label: 'Profile', icon: FaUser },
  ];

  const pageItems = [
    { path: '/about', label: 'About', icon: FaInfoCircle },
    { path: '/help', label: 'Help & Support', icon: FaQuestionCircle },
    { path: '/terms', label: 'Terms of Service', icon: FaBook },
    { path: '/privacy', label: 'Privacy Policy', icon: FaBook },
    { path: '/settings', label: 'Settings', icon: FaCog },
  ];

  const handleNavClick = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <NavigatorContainer>
      <Overlay isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      
      <Sidebar isOpen={sidebarOpen}>
        <SidebarHeader>
          <Logo>Weloop</Logo>
          <CloseButton onClick={() => setSidebarOpen(false)}>
            <FaTimes />
          </CloseButton>
        </SidebarHeader>

        <NavMenu>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavItem
                key={item.path}
                active={isActive}
                onClick={() => handleNavClick(item.path)}
              >
                <Icon />
                {item.label}
              </NavItem>
            );
          })}

          <NavSection>
            <NavSectionTitle>Pages</NavSectionTitle>
            {pageItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavItem
                  key={item.path}
                  active={isActive}
                  onClick={() => handleNavClick(item.path)}
                >
                  <Icon />
                  {item.label}
                </NavItem>
              );
            })}
          </NavSection>
        </NavMenu>

        <UserSection>
          <UserAvatar
            src={user?.meta?.profile_image || '/default-avatar.png'}
            alt="Profile"
          />
          <UserInfo>
            <UserName>{user?.meta?.name || user?.username || 'User'}</UserName>
            <UserEmail>{user?.email || 'user@example.com'}</UserEmail>
          </UserInfo>
        </UserSection>
        
        <LogoutSection>
          <LogoutButton onClick={handleLogout}>
            <FaSignOutAlt />
            Logout
          </LogoutButton>
        </LogoutSection>
      </Sidebar>

      <MainContent sidebarOpen={sidebarOpen}>
        <MobileHeader>
          <MobileMenuButton onClick={toggleSidebar}>
            <FaBars />
          </MobileMenuButton>
          <Logo>Weloop</Logo>
          <div style={{ width: 20 }}></div>
        </MobileHeader>
        
        <Outlet />
      </MainContent>
    </NavigatorContainer>
  );
};

export default AppNavigator; 